<template>
  <v-main>
    <v-container fluid>
      <!-- Header Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              title="Education Approval"
              subtitle="Review and approve pending education details"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <v-chip
                      color="warning"
                      outlined
                      small
                      class="font-weight-medium"
                    >
                      <v-icon left x-small>mdi-clock-alert</v-icon>
                      {{ pendingCount }} Pending
                    </v-chip>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-filter-variant</v-icon>
                    <span class="text-body-2 font-weight-medium">Filter Education Details</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Education Details"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    @input="handleSearch"
                  ></v-text-field>
                </v-col>

                <!-- Sort Options -->
                <v-col cols="12" md="4">
                  <v-select
                    v-model="filter.sort_by"
                    :items="sortOptions"
                    label="Sort by"
                    prepend-inner-icon="mdi-sort"
                    outlined
                    dense
                    @change="applyFilters"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-data-table
              :headers="headers"
              :items="educationDetails"
              :loading="loading"
              :items-per-page="rowsPerPage"
              :page.sync="currentPage"
              :server-items-length="totalItems"
              hide-default-footer
              class="elevation-1"
              item-key="id"
            >
              <!-- Loading slot -->
              <template #loading>
                <div class="text-center pa-4">
                  <health-loader message="Loading education details..." />
                </div>
              </template>

              <!-- No data slot -->
              <template #no-data>
                <div class="text-center pa-8">
                  <v-icon color="grey lighten-1" size="60">mdi-school-outline</v-icon>
                  <div class="text-h6 grey--text mt-4">No pending education details found</div>
                  <div class="text-body-2 grey--text">All education details have been reviewed</div>
                </div>
              </template>

              <!-- User Info Column -->
              <template #[`item.user_info`]="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="40" class="mr-3">
                    <v-icon>mdi-account</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium text-subtitle-2">
                      {{ item.user ? (item.user.first_name + ' ' + item.user.last_name) : 'N/A' }}
                    </div>
                    <div class="text-caption grey--text" v-if="item.user">
                      {{ item.user.email }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Qualification Column -->
              <template #[`item.qualification`]="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.qualification }}</div>
                  <div class="text-caption grey--text">{{ item.specialization }}</div>
                </div>
              </template>

              <!-- University Column -->
              <template #[`item.university`]="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.university }}</div>
                  <div class="text-caption grey--text">{{ item.year_of_passing }}</div>
                </div>
              </template>

              <!-- Created By Column -->
              <template #[`item.created_by`]="{ item }">
                <div v-if="item.created_by">
                  <div class="font-weight-medium">
                    {{ item.created_by.first_name + ' ' + item.created_by.last_name }}
                  </div>
                  <div class="text-caption grey--text">{{ item.created_by.email }}</div>
                </div>
                <span v-else class="grey--text">System</span>
              </template>

              <!-- Created At Column -->
              <template #[`item.created_at`]="{ item }">
                <div>
                  <div class="text-body-2">{{ formatDate(item.created_at) }}</div>
                  <div class="text-caption grey--text">{{ formatTime(item.created_at) }}</div>
                </div>
              </template>

              <!-- Actions Column -->
              <template #[`item.actions`]="{ item }">
                <div class="d-flex">
                  <v-btn
                    color="success"
                    small
                    outlined
                    class="mr-1"
                    @click="openApproveDialog(item)"
                    :loading="loadingActions[item.id]"
                  >
                    <v-icon small>mdi-check</v-icon>
                    Approve
                  </v-btn>
                  <v-btn
                    color="error"
                    small
                    outlined
                    @click="openRejectDialog(item)"
                    :loading="loadingActions[item.id]"
                  >
                    <v-icon small>mdi-close</v-icon>
                    Reject
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <!-- Custom Pagination -->
            <div class="px-4 py-2">
              <v-row align="center" justify="space-between">
                <v-col cols="12" md="6">
                  <div class="d-flex align-center">
                    <span class="text-body-2 mr-3">Rows per page:</span>
                    <v-select
                      v-model="rowsPerPage"
                      :items="[10, 25, 50, 100]"
                      dense
                      outlined
                      hide-details
                      style="max-width: 80px"
                      @change="handlePageSizeChange"
                    ></v-select>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="d-flex justify-end">
                    <v-pagination
                      v-model="currentPage"
                      :length="totalPages"
                      :total-visible="7"
                      @input="handlePageChange"
                    ></v-pagination>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Approve Dialog -->
    <v-dialog v-model="approveDialog.show" max-width="500">
      <v-card>
        <v-card-title class="text-h6 success--text">
          <v-icon left color="success">mdi-check-circle</v-icon>
          Approve Education Detail
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p>Are you sure you want to approve this education detail?</p>
            <div class="d-flex align-center mt-3">
              <v-avatar size="32" class="mr-2">
                <v-icon>mdi-account</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold">
                  {{ approveDialog.educationDetail?.user ? (approveDialog.educationDetail.user.first_name + ' ' + approveDialog.educationDetail.user.last_name) : 'N/A' }}
                </div>
                <div class="text-caption grey--text">
                  {{ approveDialog.educationDetail?.qualification }}
                </div>
              </div>
            </div>
          </div>
          
          <v-textarea
            v-model="approveDialog.remark"
            label="Approval Remark (Optional)"
            placeholder="Add any notes about this approval..."
            outlined
            rows="3"
            counter="500"
            :rules="[v => !v || v.length <= 500 || 'Remark must be 500 characters or less']"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeApproveDialog">Cancel</v-btn>
          <v-btn 
            color="success" 
            @click="approveEducationDetail"
            :loading="approveDialog.loading"
          >
            <v-icon left>mdi-check</v-icon>
            Approve
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reject Dialog -->
    <v-dialog v-model="rejectDialog.show" max-width="500">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon left color="error">mdi-close-circle</v-icon>
          Reject Education Detail
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p>Please provide a reason for rejecting this education detail:</p>
            <div class="d-flex align-center mt-3">
              <v-avatar size="32" class="mr-2">
                <v-icon>mdi-account</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold">
                  {{ rejectDialog.educationDetail?.user ? (rejectDialog.educationDetail.user.first_name + ' ' + rejectDialog.educationDetail.user.last_name) : 'N/A' }}
                </div>
                <div class="text-caption grey--text">
                  {{ rejectDialog.educationDetail?.qualification }}
                </div>
              </div>
            </div>
          </div>
          
          <v-textarea
            v-model="rejectDialog.remark"
            label="Rejection Reason *"
            placeholder="Please explain why this education detail is being rejected..."
            outlined
            rows="4"
            counter="1000"
            :rules="rejectRemarkRules"
            :error-messages="rejectDialog.errors.rejection_reason"
            required
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeRejectDialog">Cancel</v-btn>
          <v-btn 
            color="error" 
            @click="rejectEducationDetail"
            :loading="rejectDialog.loading"
            :disabled="!rejectDialog.remark || rejectDialog.remark.length < 5"
          >
            <v-icon left>mdi-close</v-icon>
            Reject
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="successSnackbar.show"
      :color="successSnackbar.color"
      :timeout="4000"
      top
    >
      {{ successSnackbar.message }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="successSnackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import axios from '../../axios'

export default {
  name: 'EducationApproval',
  data() {
    return {
      loading: false,
      educationDetails: [],
      totalItems: 0,
      currentPage: 1,
      rowsPerPage: 25,
      loadingActions: {},
      
      // Filters
      filter: {
        search_keyword: '',
        sort_by: 'created_at',
      },
      
      // Header configuration
      headers: [
        { text: 'User', value: 'user_info', sortable: false, width: '250px' },
        { text: 'Qualification', value: 'qualification', sortable: false, width: '200px' },
        { text: 'University/Year', value: 'university', sortable: false, width: '200px' },
        { text: 'Submitted By', value: 'created_by', sortable: false, width: '150px' },
        { text: 'Submitted On', value: 'created_at', width: '150px' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '200px' },
      ],
      
      // Sort options
      sortOptions: [
        { text: 'Oldest First', value: 'created_at' },
        { text: 'Newest First', value: 'created_at_desc' },
        { text: 'Qualification A-Z', value: 'qualification' },
        { text: 'Qualification Z-A', value: 'qualification_desc' },
      ],
      
      // Dialogs
      approveDialog: {
        show: false,
        educationDetail: null,
        remark: '',
        loading: false,
      },
      rejectDialog: {
        show: false,
        educationDetail: null,
        remark: '',
        loading: false,
        errors: {},
      },
      
      // Snackbar
      successSnackbar: {
        show: false,
        message: '',
        color: 'success',
      },
      
      // Search debounce
      searchTimeout: null,
    }
  },
  
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.rowsPerPage)
    },
    
    pendingCount() {
      return this.totalItems
    },
    
    hasActiveFilters() {
      return !!(this.filter.search_keyword)
    },
    
    rejectRemarkRules() {
      return [
        v => !!v || 'Rejection reason is required',
        v => (v && v.length >= 5) || 'Rejection reason must be at least 5 characters',
        v => (v && v.length <= 1000) || 'Rejection reason must be 1000 characters or less',
      ]
    },
  },
  
  watch: {
    currentPage() {
      this.fetchEducationDetails()
    },
  },
  
  async mounted() {
    await this.fetchEducationDetails()
  },
  
  methods: {
    async fetchEducationDetails() {
      try {
        this.loading = true
        
        const params = {
          page: this.currentPage,
          rowsPerPage: this.rowsPerPage,
          status: 0, // 0 = Pending
          ...this.filter,
        }
        
        // Handle sort parameters
        if (this.filter.sort_by.includes('_desc')) {
          params.sort_by = this.filter.sort_by.replace('_desc', '')
          params.sort_order = 'desc'
        } else {
          params.sort_by = this.filter.sort_by
          params.sort_order = 'asc'
        }
        
        const response = await axios.get('education_details', { params })
        
        if (response.data.success) {
          this.educationDetails = response.data.data
          this.totalItems = response.data.count
        }
      } catch (error) {
        console.error('Error fetching pending education details:', error)
        this.showSnackbar('Error loading pending education details', 'error')
      } finally {
        this.loading = false
      }
    },
    
    // Filter methods
    handleSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.fetchEducationDetails()
      }, 500)
    },
    
    applyFilters() {
      this.currentPage = 1
      this.fetchEducationDetails()
    },
    
    clearAllFilters() {
      this.filter = {
        search_keyword: '',
        sort_by: 'created_at',
      }
      this.applyFilters()
    },
    
    getActiveFiltersCount() {
      let count = 0
      if (this.filter.search_keyword) count++
      return count
    },
    
    // Pagination methods
    handlePageChange() {
      this.fetchEducationDetails()
    },
    
    handlePageSizeChange() {
      this.currentPage = 1
      this.fetchEducationDetails()
    },
    
    // Dialog methods
    openApproveDialog(educationDetail) {
      this.approveDialog = {
        show: true,
        educationDetail: educationDetail,
        remark: '',
        loading: false,
      }
    },
    
    closeApproveDialog() {
      this.approveDialog = {
        show: false,
        educationDetail: null,
        remark: '',
        loading: false,
      }
    },
    
    openRejectDialog(educationDetail) {
      this.rejectDialog = {
        show: true,
        educationDetail: educationDetail,
        remark: '',
        loading: false,
        errors: {},
      }
    },
    
    closeRejectDialog() {
      this.rejectDialog = {
        show: false,
        educationDetail: null,
        remark: '',
        loading: false,
        errors: {},
      }
    },
    
    // Approval methods
    async approveEducationDetail() {
      try {
        this.approveDialog.loading = true
        this.loadingActions[this.approveDialog.educationDetail.id] = true
        
        const data = {
          approval_remark: this.approveDialog.remark || null,
        }
        
        const response = await axios.post(`education_details/approve/${this.encryptIt(this.approveDialog.educationDetail.id)}`, data)
        
        if (response.data.success) {
          this.showSnackbar('Education detail approved successfully', 'success')
          this.closeApproveDialog()
          await this.fetchEducationDetails()
        }
      } catch (error) {
        console.error('Error approving education detail:', error)
        this.showSnackbar('Error approving education detail', 'error')
      } finally {
        this.approveDialog.loading = false
        delete this.loadingActions[this.approveDialog.educationDetail?.id]
      }
    },
    
    async rejectEducationDetail() {
      try {
        this.rejectDialog.loading = true
        this.rejectDialog.errors = {}
        this.loadingActions[this.rejectDialog.educationDetail.id] = true
        
        const data = {
          rejection_reason: this.rejectDialog.remark,
        }
        
        const response = await axios.post(`education_details/reject/${this.encryptIt(this.rejectDialog.educationDetail.id)}`, data)
        
        if (response.data.success) {
          this.showSnackbar('Education detail rejected successfully', 'success')
          this.closeRejectDialog()
          await this.fetchEducationDetails()
        }
      } catch (error) {
        console.error('Error rejecting education detail:', error)
        if (error.response?.data?.errors) {
          this.rejectDialog.errors = error.response.data.errors
        } else {
          this.showSnackbar('Error rejecting education detail', 'error')
        }
      } finally {
        this.rejectDialog.loading = false
        delete this.loadingActions[this.rejectDialog.educationDetail?.id]
      }
    },
    
    // Utility methods
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    
    showSnackbar(message, color = 'success') {
      this.successSnackbar = {
        show: true,
        message: message,
        color: color,
      }
    },
  },
}
</script>

<style scoped>
.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}
</style>